import { groupBy } from 'es-toolkit';

import { ComponentDesign, FeaturePatternTruss, isTruss } from '@paragon/contract-library';
import {
  AdditionalLoadsSchema,
  AnalogJointSchema,
  AnalogLinkSchema,
  AnalogModelSchema,
  BearingSchema,
  BraceSchema,
  CarriedComponentsSchema,
  connectorDisplayName,
  DimensionSchema,
  EndConditionSchema,
  GenericLoader,
  getLoadGroupId,
  getLoadGroupName,
  GussetSchema,
  HangerSchema,
  LoadCaseSchema,
  LoadGroupSchema,
  LoadSchema,
  ManualLoadSchema,
  MemberSchema,
  ModelChildLoader,
  MovingLoadSchema,
  PanelSchema,
  PlatePairSchema,
  SpliceSchema,
} from '@paragon/layout-models';

import {
  CustomFeaturePatternTrussDesignSchema,
  CustomProfileTrussDesignSchema,
} from '../schemas/CustomTrussDesignSchema';

export function registerBasicComponentDesignDataLoaders(
  loaders: ModelChildLoader,
  jackGirderConnectionLoader: GenericLoader,
  searchTerm: string,
) {
  loaders.add(CustomProfileTrussDesignSchema, DimensionSchema, () => [] as any[]);
  loaders.add(CustomFeaturePatternTrussDesignSchema, DimensionSchema, () => [] as any[]);

  loaders.add(AnalogModelSchema, AnalogJointSchema, (analogModel) => analogModel.analogJoints);
  loaders.add(AnalogModelSchema, AnalogLinkSchema, (analogModel) => analogModel.analogLinks);
  loaders.add(AnalogModelSchema, PanelSchema, (analogModel) => analogModel.panels);

  function getMembers(componentDesign: ComponentDesign) {
    return componentDesign.component.members.filter((member) => member.name.toLowerCase().includes(searchTerm));
  }

  function getBraces(componentDesign: ComponentDesign) {
    const braceMembers = isTruss(componentDesign.component) ? componentDesign.component.braceMembers : [];
    return braceMembers.filter((brace) => brace.name.toLowerCase().includes(searchTerm));
  }

  function getGussets(componentDesign: ComponentDesign) {
    return componentDesign.component.gussets.filter((gusset) => gusset.material.toLowerCase().includes(searchTerm));
  }

  function getPlatePairs(componentDesign: ComponentDesign) {
    return componentDesign.component.platePairs.filter((pair) => pair.name.toLowerCase().includes(searchTerm));
  }

  function getBearings(componentDesign: ComponentDesign) {
    return componentDesign.component.bearings.filter((bearing) => bearing.name.toLowerCase().includes(searchTerm));
  }

  function getHangers(componentDesign: ComponentDesign) {
    return componentDesign.component.hangers.filter((hanger) =>
      connectorDisplayName(hanger.connector).toLowerCase().includes(searchTerm),
    );
  }

  loaders.add(CustomFeaturePatternTrussDesignSchema, MemberSchema, getMembers);
  loaders.add(CustomFeaturePatternTrussDesignSchema, BraceSchema, getBraces);
  loaders.add(CustomFeaturePatternTrussDesignSchema, GussetSchema, getGussets);
  loaders.add(CustomFeaturePatternTrussDesignSchema, PlatePairSchema, getPlatePairs);
  loaders.add(CustomFeaturePatternTrussDesignSchema, BearingSchema, getBearings);
  loaders.add(
    CustomFeaturePatternTrussDesignSchema,
    SpliceSchema,
    (componentDesign) => componentDesign.component.chordSplices,
  );
  loaders.add(CustomFeaturePatternTrussDesignSchema, DimensionSchema, () => [] as any[]);
  loaders.add(CustomFeaturePatternTrussDesignSchema, EndConditionSchema, (componentDesign) => [
    (componentDesign.component as FeaturePatternTruss).rightEndCondition,
    (componentDesign.component as FeaturePatternTruss).leftEndCondition,
  ]);

  loaders.add(CustomProfileTrussDesignSchema, MemberSchema, getMembers);
  loaders.add(CustomProfileTrussDesignSchema, BraceSchema, getBraces);
  loaders.add(CustomProfileTrussDesignSchema, GussetSchema, getGussets);
  loaders.add(CustomProfileTrussDesignSchema, PlatePairSchema, getPlatePairs);
  loaders.add(CustomProfileTrussDesignSchema, BearingSchema, getBearings);
  loaders.add(
    CustomProfileTrussDesignSchema,
    SpliceSchema,
    (componentDesign) => componentDesign.component.chordSplices,
  );
  loaders.add(CustomProfileTrussDesignSchema, DimensionSchema, () => [] as any[]);

  loaders.add(AdditionalLoadsSchema, ManualLoadSchema, (additionalLoads) => additionalLoads.manualLoadSettings);
  loaders.add(AdditionalLoadsSchema, MovingLoadSchema, (additionalLoads) => additionalLoads.movingLoadSettings);

  loaders.add(LoadCaseSchema, LoadGroupSchema, (loadCase) => {
    const loadGroups = groupBy(loadCase.loads, (load) => getLoadGroupName(load));
    return Object.entries(loadGroups).map(([name, loads]) => ({
      name,
      loadCaseGuid: loadCase.guid,
      loads,
      id: getLoadGroupId(loadCase.guid, name),
    }));
  });
  loaders.add(LoadGroupSchema, LoadSchema, (loadGroup) => loadGroup.loads);

  loaders.add(CustomFeaturePatternTrussDesignSchema, CarriedComponentsSchema, jackGirderConnectionLoader);
  loaders.add(CustomProfileTrussDesignSchema, CarriedComponentsSchema, jackGirderConnectionLoader);

  loaders.add(CustomFeaturePatternTrussDesignSchema, HangerSchema, getHangers);
  loaders.add(CustomProfileTrussDesignSchema, HangerSchema, getHangers);
}
